// ── Identity Verification — Email + SMS ──────────────────────────────────
// In production:
//   Email: send via Resend/SendGrid with a 6-digit OTP
//   SMS:   send via Twilio/Vonage with a 6-digit OTP
//   Both:  store hashed OTP + expiry server-side, verify on submit

// ── OTP generation (mock) ─────────────────────────────────────────────────
export function generateOTP() {
  return String(Math.floor(100000 + Math.random() * 900000));
}

// ── DEV ONLY: mock OTP (000000 always works) ──────────────────────────────
// In production: POST /api/email/otp via api.js → Resend → user email
export function sendEmailOTP(email) {
  // Production: POST to /api/email/otp via api.js
  // The server generates OTP, stores hash+expiry, sends via Resend
  // import { sendOTPEmail } from './api';
  // return sendOTPEmail({ to: email, code: ... })  -- server generates code
  const code = generateOTP();
  // In prod: POST /api/verify/email { email } → sends email, stores hashed OTP
  // Mock: store in sessionStorage so user can "receive" it
  try { sessionStorage.setItem('beelove_email_otp', JSON.stringify({ code, email, expiresAt: Date.now() + 600000 })); } catch {}
  console.log(`[BeeLove Verify] Email OTP for ${email}: ${code}`);
  return Promise.resolve({ sent: true, maskedEmail: maskEmail(email) });
}

// ── DEV ONLY: mock OTP (000000 always works) ──────────────────────────────
// In production: POST /api/sms/otp via api.js → Twilio Verify
export function sendSmsOTP(phone) {
  // Production: POST to /api/sms/otp via api.js
  // The server calls Twilio Verify API directly (never expose Twilio creds client-side)
  const code = generateOTP();
  try { sessionStorage.setItem('beelove_sms_otp', JSON.stringify({ code, phone, expiresAt: Date.now() + 600000 })); } catch {}
  console.log(`[BeeLove Verify] SMS OTP for ${phone}: ${code}`);
  return Promise.resolve({ sent: true, maskedPhone: maskPhone(phone) });
}

// ── Mock: verify OTP ──────────────────────────────────────────────────────
export function verifyEmailOTP(email, inputCode) {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      try {
        const stored = JSON.parse(sessionStorage.getItem('beelove_email_otp') || 'null');
        if (!stored) return reject({ code: 'expired' });
        if (Date.now() > stored.expiresAt) return reject({ code: 'expired' });
        // Mock: accept '000000' as universal test code, or the actual generated code
        if (inputCode === '000000' || inputCode === stored.code) {
          sessionStorage.removeItem('beelove_email_otp');
          return resolve({ verified: true });
        }
        reject({ code: 'invalid' });
      } catch { reject({ code: 'error' }); }
    }, 900);
  });
}

export function verifySmsOTP(phone, inputCode) {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      try {
        const stored = JSON.parse(sessionStorage.getItem('beelove_sms_otp') || 'null');
        if (!stored) return reject({ code: 'expired' });
        if (Date.now() > stored.expiresAt) return reject({ code: 'expired' });
        if (inputCode === '000000' || inputCode === stored.code) {
          sessionStorage.removeItem('beelove_sms_otp');
          return resolve({ verified: true });
        }
        reject({ code: 'invalid' });
      } catch { reject({ code: 'error' }); }
    }, 900);
  });
}

// ── Helpers ───────────────────────────────────────────────────────────────
export function maskEmail(email) {
  const [user, domain] = email.split('@');
  return `${user[0]}${'*'.repeat(Math.max(1, user.length - 2))}${user.slice(-1)}@${domain}`;
}

export function maskPhone(phone) {
  const digits = phone.replace(/\D/g, '');
  return `${'*'.repeat(digits.length - 4)}${digits.slice(-4)}`;
}

export function formatPhone(value) {
  const d = value.replace(/\D/g, '').slice(0, 11);
  if (d.length <= 1) return d.startsWith('1') ? `+1 ` : d;
  const local = d.startsWith('1') ? d.slice(1) : d;
  if (local.length <= 3) return `+1 (${local}`;
  if (local.length <= 6) return `+1 (${local.slice(0,3)}) ${local.slice(3)}`;
  if (local.length <= 10) return `+1 (${local.slice(0,3)}) ${local.slice(3,6)}-${local.slice(6)}`;
  return `+1 (${local.slice(0,3)}) ${local.slice(3,6)}-${local.slice(6,10)}`;
}

// ── Persist verification status ───────────────────────────────────────────
// Verification status — stored in Supabase verifications table
// Use: await db.verifications()?.select('type,verified_at').eq('user_id', uid)
export function saveVerificationStatus(type, value) {
  // Supabase handles this via Edge Function /sms/verify
  // Local cache for immediate UI update only
  try {
    const v = JSON.parse(sessionStorage.getItem('beeloveVerified') || '{}');
    v[type] = { value, verifiedAt: new Date().toISOString() };
    sessionStorage.setItem('beeloveVerified', JSON.stringify(v));
  } catch {}
}

export function getVerificationStatus() {
  try { return JSON.parse(sessionStorage.getItem('beeloveVerified') || '{}'); }
  catch { return {}; }
}
